import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, User, Tag, Facebook, Instagram, Share2, MapPin, Star, Heart, Car, Shield, AlertTriangle } from 'lucide-react';
import { motion } from 'framer-motion';
import { FacebookShareButton, TwitterShareButton, WhatsappShareButton } from 'react-share';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';

const SelfDriveSafariTips = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Essential Self-Drive Safari Tips for Uganda | TravelCord Blog';
  }, []);

  const handleShare = async (platform: string) => {
    const url = window.location.href;
    const title = "Essential Self-Drive Safari Tips for Uganda";

    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'instagram':
        alert('Follow us on Instagram @travelcord.guide');
        break;
      default:
        if (navigator.share) {
          try {
            await navigator.share({ title, url });
          } catch (error) {
            console.error('Error sharing:', error);
          }
        }
    }
  };

  return (
    <>
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white">
        <div className="absolute inset-0">
          <img 
            src="https://imgur.com/TvdP6Mt.jpg"
            alt="Self-Drive Safari in Uganda"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-black/80"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
          <div className="mb-4">
            <Link to="/blog" className="inline-flex items-center text-white/90 hover:text-white">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Blog
            </Link>
          </div>
          
          <div className="max-w-3xl">
            <div className="flex items-center mb-4 space-x-4">
              <img 
                src="https://imgur.com/9i40c9u.jpg"
                alt="Daniel Opio"
                className="h-12 w-12 rounded-full border-2 border-white"
              />
              <div>
                <h3 className="text-lg font-medium">Daniel Opio</h3>
                <p className="text-white/80 text-sm">Self-Drive Safari Expert</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="inline-flex items-center bg-ta-green/20 rounded-full px-2.5 py-0.5 text-xs text-ta-green mb-4 backdrop-blur-sm">
                Travel Tips
              </div>
              
              <h1 className="text-4xl font-bold">Essential Self-Drive Safari Tips for Uganda</h1>
              
              <div className="flex flex-wrap gap-4 text-sm text-white/80">
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  March 22, 2025
                </div>
                <div className="flex items-center">
                  <Clock className="h-4 w-4 mr-1" />
                  9 min read
                </div>
                <div className="flex items-center">
                  <MapPin className="h-4 w-4 mr-1" />
                  Uganda
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="prose max-w-none">
                <p className="lead text-lg text-gray-600">
                  Embarking on a self-drive safari in Uganda offers unparalleled freedom to explore the Pearl of Africa at your own pace. From the savanna plains of Queen Elizabeth National Park to the dense forests of Bwindi, having your own vehicle allows for a personalized adventure. However, it also comes with unique challenges and considerations. This guide provides essential tips to ensure your self-drive safari is safe, enjoyable, and memorable.
                </p>

                <h2>Choosing the Right Vehicle</h2>
                <p>
                  The foundation of a successful self-drive safari is selecting the appropriate vehicle:
                </p>
                <ul>
                  <li><strong>4x4 is Non-Negotiable</strong> - Uganda's national parks have unpaved roads that can become challenging, especially during rainy seasons. A 4x4 vehicle with high clearance is essential.</li>
                  <li><strong>Popular Options</strong> - Toyota Land Cruisers and Land Cruiser Prados are ideal for their reliability and performance on rough terrain.</li>
                  <li><strong>Size Considerations</strong> - Choose a vehicle size based on your group and luggage. Remember that larger vehicles may be more challenging to maneuver on narrow forest roads.</li>
                  <li><strong>Rental Companies</strong> - Work with reputable rental companies that provide well-maintained vehicles and 24/7 support.</li>
                </ul>

                <div className="my-8">
                  <img 
                    src="https://imgur.com/YABl8r4.jpg" 
                    alt="4x4 Vehicle for Uganda Safari"
                    className="rounded-lg w-full"
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    A 4x4 vehicle is essential for navigating Uganda's national park roads
                  </p>
                </div>

                <h2>Planning Your Route</h2>
                <p>
                  Careful planning is crucial for a smooth self-drive experience:
                </p>
                <ul>
                  <li><strong>Realistic Distances</strong> - Don't underestimate travel times. A 200km journey can take 4-5 hours due to road conditions.</li>
                  <li><strong>Buffer Days</strong> - Include buffer days in your itinerary for unexpected delays or to extend stays in places you particularly enjoy.</li>
                  <li><strong>Park Permits</strong> - Secure necessary permits in advance, especially for gorilla and chimpanzee trekking which book out months ahead.</li>
                  <li><strong>Accommodation</strong> - Book lodges in advance, particularly during high season (June-September and December-February).</li>
                </ul>

                <h2>Navigation and Communication</h2>
                <p>
                  Staying connected and finding your way:
                </p>
                <ul>
                  <li><strong>Offline Maps</strong> - Download Google Maps or Maps.me for offline navigation. Cell service can be unreliable in remote areas.</li>
                  <li><strong>Physical Maps</strong> - Carry physical maps as backup.</li>
                  <li><strong>Local SIM Card</strong> - Purchase a local SIM card with data for when you do have service.</li>
                  <li><strong>Emergency Contacts</strong> - Keep a list of emergency contacts including your rental company, accommodation, and park authorities.</li>
                </ul>

                <div className="bg-yellow-50 p-6 rounded-lg my-8 border-l-4 border-yellow-500">
                  <div className="flex">
                    <AlertTriangle className="h-6 w-6 text-yellow-500 mr-3 flex-shrink-0" />
                    <div>
                      <h3 className="text-lg font-semibold text-yellow-800 mb-2">Safety First</h3>
                      <ul className="space-y-2 text-yellow-700">
                        <li>Always inform someone of your daily route and expected arrival time</li>
                        <li>Avoid driving at night, especially in or near national parks</li>
                        <li>Carry extra water, snacks, and basic first aid supplies</li>
                        <li>Never leave valuables visible in your parked vehicle</li>
                        <li>Keep your fuel tank at least half full at all times</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <h2>Wildlife Viewing Etiquette</h2>
                <p>
                  Responsible wildlife viewing enhances your experience and protects animals:
                </p>
                <ul>
                  <li><strong>Maintain Distance</strong> - Stay at least 20 meters from wildlife and never attempt to approach animals.</li>
                  <li><strong>Stay in Vehicle</strong> - Remain in your vehicle except in designated areas.</li>
                  <li><strong>No Off-Road Driving</strong> - Stick to established tracks to protect the environment.</li>
                  <li><strong>Quiet Observation</strong> - Keep noise to a minimum to avoid disturbing wildlife.</li>
                  <li><strong>No Feeding</strong> - Never feed wild animals as it disrupts their natural behaviors.</li>
                </ul>

                <h2>Practical Tips for the Road</h2>
                <ul>
                  <li><strong>Drive on the Left</strong> - Uganda follows left-hand driving.</li>
                  <li><strong>Speed Limits</strong> - Observe posted limits: typically 50 km/h in urban areas and 80 km/h on highways.</li>
                  <li><strong>Road Conditions</strong> - Be prepared for potholes, unpaved sections, and occasional livestock on roads.</li>
                  <li><strong>Local Driving Culture</strong> - Ugandan drivers may not always follow traffic rules strictly. Drive defensively.</li>
                  <li><strong>Police Checkpoints</strong> - You may encounter police checkpoints. Have your documents ready and be respectful.</li>
                </ul>

                <h2>What to Pack for Your Self-Drive Safari</h2>
                <ul>
                  <li><strong>Documents</strong> - Driver's license, International Driving Permit, passport, vehicle rental agreement, and insurance papers.</li>
                  <li><strong>Navigation</strong> - GPS device or smartphone with offline maps.</li>
                  <li><strong>Emergency Kit</strong> - First aid supplies, flashlight, multi-tool, and basic vehicle repair items.</li>
                  <li><strong>Communication</strong> - Fully charged phone, power bank, and car charger.</li>
                  <li><strong>Comfort Items</strong> - Sunglasses, hat, sunscreen, insect repellent, and wet wipes.</li>
                </ul>

                <div className="bg-gray-50 p-6 rounded-lg my-8">
                  <h3 className="text-lg font-semibold mb-4">Recommended Self-Drive Routes</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <Star className="h-5 w-5 text-ta-green mr-2 flex-shrink-0" />
                      <div>
                        <strong>The Southern Circuit (10-14 days):</strong> 
                        <span className="block text-gray-600 mt-1">Kampala → Lake Mburo → Queen Elizabeth → Bwindi → Lake Bunyonyi → Lake Mutanda → Kampala</span>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <Star className="h-5 w-5 text-ta-green mr-2 flex-shrink-0" />
                      <div>
                        <strong>The Northern Explorer (7-10 days):</strong> 
                        <span className="block text-gray-600 mt-1">Kampala → Murchison Falls → Kidepo Valley → Sipi Falls → Jinja → Kampala</span>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <Star className="h-5 w-5 text-ta-green mr-2 flex-shrink-0" />
                      <div>
                        <strong>The Primate Circuit (5-7 days):</strong> 
                        <span className="block text-gray-600 mt-1">Kampala → Kibale Forest → Queen Elizabeth → Bwindi → Kampala</span>
                      </div>
                    </li>
                  </ul>
                </div>

                <h2>Final Thoughts</h2>
                <p>
                  A self-drive safari in Uganda offers an intimate connection with the country's landscapes and wildlife that few other experiences can match. With proper preparation, respect for wildlife and local communities, and a spirit of adventure, your journey through the Pearl of Africa will be unforgettable.
                </p>
                <p>
                  Remember that flexibility is key – sometimes the most memorable experiences come from unexpected detours and discoveries. Embrace the adventure, drive safely, and take time to truly absorb the natural beauty and cultural richness that Uganda has to offer.
                </p>
              </div>

              {/* Tags */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <div className="flex flex-wrap gap-2">
                  <span className="text-sm text-gray-600 mr-2">Tags:</span>
                  {["Self-Drive Safari", "Uganda Travel", "Safari Tips", "Wildlife Viewing", "Road Trip"].map((tag, index) => (
                    <span 
                      key={index}
                      className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-ta-green hover:text-white transition-colors cursor-pointer"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Share Buttons */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <h3 className="text-lg font-semibold mb-4">Share this article</h3>
                <div className="flex space-x-4">
                  <FacebookShareButton
                    url={window.location.href}
                    quote="Essential Self-Drive Safari Tips for Uganda"
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Facebook className="h-5 w-5 mr-2" />
                    Facebook
                  </FacebookShareButton>
                  <TwitterShareButton
                    url={window.location.href}
                    title="Essential Self-Drive Safari Tips for Uganda"
                    className="flex items-center px-4 py-2 bg-blue-400 text-white rounded-lg hover:bg-blue-500 transition-colors"
                  >
                    <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                    Twitter
                  </TwitterShareButton>
                  <WhatsappShareButton
                    url={window.location.href}
                    title="Essential Self-Drive Safari Tips for Uganda"
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Share2 className="h-5 w-5 mr-2" />
                    Share
                  </WhatsappShareButton>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* Author Card */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-center mb-4">
                    <img 
                      src="https://imgur.com/9i40c9u.jpg"
                      alt="Daniel Opio"
                      className="h-16 w-16 rounded-full"
                    />
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold">Daniel Opio</h3>
                      <p className="text-gray-600">Self-Drive Safari Expert</p>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm mb-4">
                    Daniel has been organizing self-drive safaris across Uganda for over 7 years and has personally driven more than 50,000 kilometers on Uganda's roads.
                  </p>
                  <button className="w-full py-2 bg-ta-green text-white rounded-lg hover:bg-ta-green-dark transition-colors">
                    Follow
                  </button>
                </div>

                {/* Table of Contents */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold mb-4">Table of Contents</h3>
                  <nav className="space-y-2">
                    <a href="#choosing-vehicle" className="block text-gray-600 hover:text-ta-green">Choosing the Right Vehicle</a>
                    <a href="#planning-route" className="block text-gray-600 hover:text-ta-green">Planning Your Route</a>
                    <a href="#navigation" className="block text-gray-600 hover:text-ta-green">Navigation and Communication</a>
                    <a href="#safety" className="block text-gray-600 hover:text-ta-green">Safety First</a>
                    <a href="#wildlife-etiquette" className="block text-gray-600 hover:text-ta-green">Wildlife Viewing Etiquette</a>
                    <a href="#road-tips" className="block text-gray-600 hover:text-ta-green">Practical Road Tips</a>
                    <a href="#packing" className="block text-gray-600 hover:text-ta-green">What to Pack</a>
                    <a href="#routes" className="block text-gray-600 hover:text-ta-green">Recommended Routes</a>
                  </nav>
                </div>

                {/* Related Articles */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold mb-4">Related Articles</h3>
                  <div className="space-y-4">
                    <Link to="/blog/best-time-visit-uganda" className="block group">
                      <div className="flex items-start">
                        <img 
                          src="https://imgur.com/8u8WjGG.jpg"
                          alt="Best Time to Visit Uganda"
                          className="w-20 h-20 rounded-lg object-cover"
                        />
                        <div className="ml-3">
                          <h4 className="text-sm font-medium text-gray-900 group-hover:text-ta-green">
                            Best Time to Visit Uganda
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">12 min read</p>
                        </div>
                      </div>
                    </Link>
                    <Link to="/blog/safari-photography-guide" className="block group">
                      <div className="flex items-start">
                        <img 
                          src="https://imgur.com/Rl4EEiV.jpg"
                          alt="Safari Photography Guide"
                          className="w-20 h-20 rounded-lg object-cover"
                        />
                        <div className="ml-3">
                          <h4 className="text-sm font-medium text-gray-900 group-hover:text-ta-green">
                            The Complete Safari Photography Guide
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">10 min read</p>
                        </div>
                      </div>
                    </Link>
                  </div>
                </div>

                {/* CTA */}
                <div className="bg-ta-green/10 rounded-lg border border-ta-green/20 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Ready for Your Self-Drive Adventure?</h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Explore our curated self-drive safari packages with 4x4 vehicles, detailed route maps, and 24/7 support.
                  </p>
                  <Link 
                    to="/self-drive-safaris"
                    className="block w-full py-2 bg-ta-green text-white text-center rounded-lg hover:bg-ta-green-dark transition-colors"
                  >
                    View Self-Drive Packages
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default SelfDriveSafariTips;